/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import HousienBrandLogo from '@/shared/ui/components/basic/logos/HousienBrandLogo';
import { createParticles, drawFrame } from './WelcomeBreathingPulse.helpers';
import { Text, Title } from '@/shared/ui';

interface WelcomeBreathingPulseProps {
  userName: string;
  onComplete: () => void;
}

const TOTAL_DURATION_MS = 6200;

const WelcomeBreathingPulse: React.FC<WelcomeBreathingPulseProps> = ({ userName, onComplete }) => {
  const { t } = useTranslation('@pages/welcome');
  const [phase, setPhase] = useState(0);
  const [fadeOut, setFadeOut] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef(0);
  const startRef = useRef(0);
  const completeFired = useRef(false);

  const handleComplete = useCallback(() => {
    if (!completeFired.current) {
      completeFired.current = true;
      onComplete();
    }
  }, [onComplete]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvas.offsetWidth * dpr;
    canvas.height = canvas.offsetHeight * dpr;
    ctx.scale(dpr, dpr);

    const W = canvas.offsetWidth;
    const H = canvas.offsetHeight;

    const particles = createParticles(W, H);

    startRef.current = 0;

    const draw = (ts: number) => {
      if (!startRef.current) startRef.current = ts;
      const elapsed = (ts - startRef.current) / 1000;

      drawFrame(ctx, particles, elapsed, W, H);

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);

    const timers = [
      setTimeout(() => setPhase(1), 3100),
      setTimeout(() => setPhase(2), 3700),
      setTimeout(() => setPhase(3), 4200),
      setTimeout(() => setFadeOut(true), TOTAL_DURATION_MS),
    ];

    const completeTimer = setTimeout(handleComplete, TOTAL_DURATION_MS + 1800);

    return () => {
      cancelAnimationFrame(rafRef.current);
      timers.forEach(clearTimeout);
      clearTimeout(completeTimer);
    };
  }, [handleComplete]);

  return createPortal(
    <div
      className="fixed inset-0 flex flex-col items-center justify-center overflow-hidden"
      style={{
        zIndex: 99999,
        background: 'var(--surface-secondary, #faf7f4)',
        opacity: fadeOut ? 0 : 1,
        transition: 'opacity 1.8s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
      onTransitionEnd={() => {
        if (fadeOut) handleComplete();
      }}
    >
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />

      <div className="relative z-10 flex flex-col items-center gap-(--space-5)">
        <div
          style={{
            opacity: phase >= 1 ? 1 : 0,
            transform: `scale(${phase >= 1 ? 1 : 0.9}) translateY(${phase >= 1 ? 0 : 10}px)`,
            transition: 'opacity 1.1s ease, transform 1.3s cubic-bezier(0.16, 1, 0.3, 1)',
            filter: phase >= 1 ? 'none' : 'blur(4px)',
          }}
        >
          <HousienBrandLogo variant="full" height={76} />
        </div>

        <div
          className="h-px"
          style={{
            background: 'var(--seed-base)',
            width: phase >= 2 ? '90px' : '0px',
            opacity: phase >= 2 ? 0.6 : 0,
            transition: 'width 0.8s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.4s ease',
          }}
        />

        <div
          className="flex flex-col items-center gap-(--space-1)"
          style={{
            opacity: phase >= 3 ? 1 : 0,
            transform: `translateY(${phase >= 3 ? 0 : 12}px)`,
            transition: 'opacity 1s ease, transform 1.2s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          <Text
            className="text-center leading-snug"
            style={{
              fontSize: 'clamp(1.1rem, 2.5vw, 1.4rem)',
              fontWeight: 300,
              color: 'var(--seed-base)',
              letterSpacing: '0.01em',
              opacity: 0.7,
            }}
          >
            {t('welcome.greeting')}
          </Text>
          <Title order={1}
            className="text-center leading-snug"
            style={{
              fontSize: 'clamp(1.8rem, 5vw, 2.8rem)',
              fontWeight: 200,
              color: 'var(--seed-base)',
              letterSpacing: '-0.02em',
            }}
          >
            {t('welcome.name', { name: userName })}
          </Title>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default WelcomeBreathingPulse;
